package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.FlowRowScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppBadge
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.ComplaintStatus
import br.com.vvrefeicoes.portal.domain.model.CountItem
import br.com.vvrefeicoes.portal.domain.model.CustomerComplaint
import br.com.vvrefeicoes.portal.domain.model.MonthCount
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalPeriod
import com.composables.icons.lucide.Ban
import com.composables.icons.lucide.Check
import com.composables.icons.lucide.CircleAlert
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.MessageSquareWarning
import com.composables.icons.lucide.Tag
import io.github.koalaplot.core.bar.HorizontalBarPlot
import io.github.koalaplot.core.line.LinePlot
import io.github.koalaplot.core.style.LineStyle
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.Point
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_complaint_col_date
import portalvv.shared.generated.resources.operational_complaint_col_description
import portalvv.shared.generated.resources.operational_complaint_col_nature
import portalvv.shared.generated.resources.operational_complaint_col_owner
import portalvv.shared.generated.resources.operational_complaint_col_service
import portalvv.shared.generated.resources.operational_complaint_col_shift
import portalvv.shared.generated.resources.operational_complaint_col_status
import portalvv.shared.generated.resources.operational_complaint_col_supplier
import portalvv.shared.generated.resources.operational_complaint_col_time
import portalvv.shared.generated.resources.operational_complaint_col_treatment
import portalvv.shared.generated.resources.operational_complaint_empty
import portalvv.shared.generated.resources.operational_complaint_status_closed
import portalvv.shared.generated.resources.operational_complaint_status_in_progress
import portalvv.shared.generated.resources.operational_complaint_status_open
import portalvv.shared.generated.resources.operational_complaint_status_resolved
import portalvv.shared.generated.resources.operational_complaints_by_nature_title
import portalvv.shared.generated.resources.operational_complaints_by_service_title
import portalvv.shared.generated.resources.operational_complaints_by_shift_title
import portalvv.shared.generated.resources.operational_complaints_by_unit_title
import portalvv.shared.generated.resources.operational_complaints_evolution_legend
import portalvv.shared.generated.resources.operational_complaints_evolution_title
import portalvv.shared.generated.resources.operational_complaints_total_occurrences
import kotlin.math.roundToLong

private val CHART_COLORS =
    listOf(
        AppPalette.Primary500,
        AppPalette.Accent500,
        AppPalette.Info500,
        AppPalette.Warning500,
        AppPalette.Success500,
        AppPalette.Error500,
        AppPalette.Primary900,
        AppPalette.Accent900,
    )

private const val W_DATE = 0.9f
private const val W_TIME = 0.7f
private const val W_SERVICE = 1f
private const val W_STATUS = 1f
private const val W_SUPPLIER = 1.2f
private const val W_SHIFT = 0.9f
private const val W_NATURE = 1.3f
private const val W_DESCRIPTION = 2.2f
private const val W_TREATMENT = 2.2f
private const val W_OWNER = 1.4f

@Composable
fun ComplaintsContent() {
    val controller = rememberComplaintsDashboardController()
    val filters = LocalGlobalFilters.current
    val period = LocalGlobalPeriod.current

    LaunchedEffect(controller) {
        controller.init()
    }

    LaunchedEffect(period.selection) {
        controller.applyPeriod(period.selection)
    }

    LaunchedEffect(filters.unit, filters.manager, filters.director, filters.units) {
        controller.applyFilter(filters.selectedUnit, filters.selectedManager, filters.selectedDirector, filters.units)
    }

    when {
        controller.loading ->
            Box(
                modifier = Modifier.fillMaxWidth().height(200.dp),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        controller.records.isEmpty() ->
            EmptyState(message = stringResource(Res.string.operational_complaint_empty))
        else -> {
            StatusIndicators(records = controller.selectedRecords)
            EvolutionCard(evolution = controller.evolution)
            DistributionCards(
                byShift = controller.byShift,
                byService = controller.byService,
                byNature = controller.byNature,
            )
            ByUnitCard(items = controller.byUnit)
            ComplaintsTable(records = controller.selectedRecords)
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun StatusIndicators(records: List<CustomerComplaint>) {
    FlowRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        maxItemsInEachRow = 5,
    ) {
        StatusTile(
            icon = Lucide.MessageSquareWarning,
            iconColor = AppPalette.Primary500,
            iconContainerColor = AppPalette.Primary100,
            label = stringResource(Res.string.operational_complaints_total_occurrences),
            count = records.size,
        )
        StatusTile(
            icon = Lucide.CircleAlert,
            iconColor = AppPalette.Warning500,
            iconContainerColor = AppPalette.Warning100,
            label = stringResource(Res.string.operational_complaint_status_open),
            count = records.count { it.status == ComplaintStatus.OPEN },
        )
        StatusTile(
            icon = Lucide.Tag,
            iconColor = AppPalette.Info500,
            iconContainerColor = AppPalette.Info100,
            label = stringResource(Res.string.operational_complaint_status_in_progress),
            count = records.count { it.status == ComplaintStatus.IN_PROGRESS },
        )
        StatusTile(
            icon = Lucide.Check,
            iconColor = AppPalette.Success500,
            iconContainerColor = AppPalette.Success100,
            label = stringResource(Res.string.operational_complaint_status_resolved),
            count = records.count { it.status == ComplaintStatus.RESOLVED },
        )
        StatusTile(
            icon = Lucide.Ban,
            iconColor = AppPalette.Neutral500,
            iconContainerColor = AppPalette.Neutral200,
            label = stringResource(Res.string.operational_complaint_status_closed),
            count = records.count { it.status == ComplaintStatus.CLOSED },
        )
    }
}

@Composable
private fun FlowRowScope.StatusTile(
    icon: ImageVector,
    iconColor: Color,
    iconContainerColor: Color,
    label: String,
    count: Int,
) {
    Surface(
        modifier = Modifier.weight(1f).height(84.dp),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            Surface(
                shape = AppShapes.small,
                color = iconContainerColor,
                modifier = Modifier.size(32.dp),
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        tint = iconColor,
                        modifier = Modifier.size(16.dp),
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text(
                    text = label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = count.toString(),
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
private fun EvolutionCard(evolution: List<MonthCount>) {
    ChartCard(title = stringResource(Res.string.operational_complaints_evolution_title)) {
        SeriesLegend(
            entries = listOf(LegendEntry(AppPalette.Primary500, stringResource(Res.string.operational_complaints_evolution_legend))),
        )
        if (evolution.isEmpty()) {
            EmptyChart()
        } else {
            val labels = evolution.map { MonthCalendar.shortLabel(it.period) }
            val values = evolution.map { it.count.toFloat() }
            val points = evolution.mapIndexed { index, _ -> Point(labels[index], values[index]) }
            val maxValue = values.maxOrNull()?.coerceAtLeast(1f) ?: 1f
            XYGraph(
                gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                xAxisModel = CategoryAxisModel(labels),
                yAxisModel = rememberValueAxis(maxValue, divisions = 4, headroom = 1.5f),
                xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                yAxisContent = rememberAxisContent(labels = { value -> Text(plainNumber(value.roundToLong()), style = MaterialTheme.typography.labelSmall) }),
                modifier = Modifier.fillMaxWidth().height(260.dp),
            ) {
                LinePlot(
                    data = points,
                    lineStyle = LineStyle(brush = SolidColor(AppPalette.Primary500), strokeWidth = 2.dp),
                    symbol = { point ->
                        val item = evolution.getOrNull(labels.indexOf(point.x))
                        HoverTooltip(text = "${point.x}: ${plainNumber((item?.count ?: 0).toLong())}") {
                            LinePointLabel(plainNumber((item?.count ?: 0).toLong()), AppPalette.Primary500)
                        }
                    },
                )
            }
        }
    }
}

@Composable
private fun DistributionCards(
    byShift: List<CountItem>,
    byService: List<CountItem>,
    byNature: List<CountItem>,
) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        if (maxWidth >= 1000.dp) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
                DistributionCard(stringResource(Res.string.operational_complaints_by_shift_title), byShift, Modifier.weight(1f))
                DistributionCard(stringResource(Res.string.operational_complaints_by_service_title), byService, Modifier.weight(1f))
                DistributionCard(stringResource(Res.string.operational_complaints_by_nature_title), byNature, Modifier.weight(1f))
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
                DistributionCard(stringResource(Res.string.operational_complaints_by_shift_title), byShift, Modifier.fillMaxWidth())
                DistributionCard(stringResource(Res.string.operational_complaints_by_service_title), byService, Modifier.fillMaxWidth())
                DistributionCard(stringResource(Res.string.operational_complaints_by_nature_title), byNature, Modifier.fillMaxWidth())
            }
        }
    }
}

@Composable
private fun DistributionCard(
    title: String,
    items: List<CountItem>,
    modifier: Modifier,
) {
    val total = items.sumOf { it.count }.coerceAtLeast(1)
    ChartCard(title = title, modifier = modifier) {
        if (items.isEmpty()) {
            EmptyChart()
        } else {
            DonutChart(
                slices =
                    items.mapIndexed { index, item ->
                        DonutSlice(item.label, item.count.toLong(), CHART_COLORS[index % CHART_COLORS.size])
                    },
                modifier = Modifier.align(Alignment.CenterHorizontally).size(180.dp),
                formatValue = { it.toString() },
            )
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                items.forEachIndexed { index, item ->
                    LegendRow(
                        color = CHART_COLORS[index % CHART_COLORS.size],
                        label = item.label,
                        value = "${item.count} (${percentText(item.count.toDouble() / total * 100)})",
                    )
                }
            }
        }
    }
}

@Composable
private fun LegendRow(
    color: Color,
    label: String,
    value: String,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Box(modifier = Modifier.size(12.dp).background(color, AppShapes.small))
        Text(
            text = label,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f),
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun ByUnitCard(items: List<CountItem>) {
    val total = items.sumOf { it.count }.coerceAtLeast(1)
    ChartCard(title = stringResource(Res.string.operational_complaints_by_unit_title)) {
        if (items.isEmpty()) {
            EmptyChart()
        } else {
            val names = items.reversed().map { it.label }
            val values = items.reversed().map { it.count.toFloat() }
            val maxValue = values.maxOrNull()?.coerceAtLeast(1f) ?: 1f
            XYGraph(
                gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                xAxisModel = rememberValueAxis(maxValue, headroom = 1.2f),
                yAxisModel = CategoryAxisModel(names),
                xAxisContent = rememberAxisContent(labels = { value -> Text(plainNumber(value.roundToLong()), style = MaterialTheme.typography.labelSmall) }),
                yAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                modifier = Modifier.fillMaxWidth().height((names.size * 36 + 48).dp),
            ) {
                val ordered = items.reversed()
                HorizontalBarPlot(
                    xData = values,
                    yData = names,
                    barWidth = 0.6f,
                    bar = { index, _, value ->
                        val item = ordered.getOrNull(index)
                        val share = if (item == null) 0.0 else item.count.toDouble() / total * 100
                        HoverTooltip(text = "${value.y} — ${item?.count ?: 0} (${percentText(share)})") {
                            ValueBar(color = AppPalette.Primary500, label = compactNumber((item?.count ?: 0).toDouble()), horizontal = true)
                        }
                    },
                )
            }
        }
    }
}

@Composable
private fun ComplaintsTable(records: List<CustomerComplaint>) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            ComplaintsHeaderRow()
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            if (records.isEmpty()) {
                EmptyState(message = stringResource(Res.string.operational_complaint_empty))
            } else {
                records.forEachIndexed { index, record ->
                    ComplaintsBodyRow(record = record)
                    if (index < records.lastIndex) {
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    }
                }
            }
        }
    }
}

@Composable
private fun ComplaintsHeaderRow() {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space12),
        verticalAlignment = Alignment.Top,
    ) {
        HeaderCell(stringResource(Res.string.operational_complaint_col_date), W_DATE)
        HeaderCell(stringResource(Res.string.operational_complaint_col_time), W_TIME)
        HeaderCell(stringResource(Res.string.operational_complaint_col_service), W_SERVICE)
        HeaderCell(stringResource(Res.string.operational_complaint_col_status), W_STATUS)
        HeaderCell(stringResource(Res.string.operational_complaint_col_supplier), W_SUPPLIER)
        HeaderCell(stringResource(Res.string.operational_complaint_col_shift), W_SHIFT)
        HeaderCell(stringResource(Res.string.operational_complaint_col_nature), W_NATURE)
        HeaderCell(stringResource(Res.string.operational_complaint_col_description), W_DESCRIPTION)
        HeaderCell(stringResource(Res.string.operational_complaint_col_treatment), W_TREATMENT)
        HeaderCell(stringResource(Res.string.operational_complaint_col_owner), W_OWNER)
    }
}

@Composable
private fun ComplaintsBodyRow(record: CustomerComplaint) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space12),
        verticalAlignment = Alignment.Top,
    ) {
        BodyCell(dateLabel(record.date), W_DATE)
        BodyCell(record.time, W_TIME)
        BodyCell(record.service, W_SERVICE)
        Box(modifier = Modifier.weight(W_STATUS).padding(horizontal = AppSpacing.space8)) {
            StatusBadge(record.status)
        }
        BodyCell(record.supplier, W_SUPPLIER)
        BodyCell(record.shift, W_SHIFT)
        BodyCell(record.nature, W_NATURE)
        LongTextCell(record.description, W_DESCRIPTION)
        LongTextCell(record.treatment, W_TREATMENT)
        BodyCell(record.treatmentOwner, W_OWNER)
    }
}

@Composable
private fun RowScope.HeaderCell(
    text: String,
    weight: Float,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

@Composable
private fun RowScope.BodyCell(
    text: String,
    weight: Float,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurface,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

@Composable
private fun RowScope.LongTextCell(
    text: String,
    weight: Float,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurface,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

@Composable
private fun StatusBadge(status: ComplaintStatus) {
    val colors = status.badgeColors()
    AppBadge(
        text = status.label(),
        containerColor = colors.first,
        contentColor = colors.second,
    )
}

@Composable
private fun ComplaintStatus.label(): String =
    when (this) {
        ComplaintStatus.OPEN -> stringResource(Res.string.operational_complaint_status_open)
        ComplaintStatus.IN_PROGRESS -> stringResource(Res.string.operational_complaint_status_in_progress)
        ComplaintStatus.RESOLVED -> stringResource(Res.string.operational_complaint_status_resolved)
        ComplaintStatus.CLOSED -> stringResource(Res.string.operational_complaint_status_closed)
    }

private fun ComplaintStatus.badgeColors(): Pair<Color, Color> =
    when (this) {
        ComplaintStatus.OPEN -> AppPalette.Warning100 to AppPalette.Warning900
        ComplaintStatus.IN_PROGRESS -> AppPalette.Info100 to AppPalette.Info900
        ComplaintStatus.RESOLVED -> AppPalette.Success100 to AppPalette.Success900
        ComplaintStatus.CLOSED -> AppPalette.Neutral200 to AppPalette.Neutral900
    }

@Composable
private fun EmptyChart() {
    Text(
        text = "—",
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
}

private fun dateLabel(iso: String): String = if (iso.length >= 10) "${iso.substring(8, 10)}/${iso.substring(5, 7)}/${iso.substring(0, 4)}" else iso

private fun percentText(value: Double): String {
    val scaled = (value * 10).toLong()
    return "${scaled / 10},${kotlin.math.abs(scaled % 10)}%"
}
